import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import { usePermissions } from "../../hooks/usePermissions";
import Pagination from "../../components/Pagination";
import ConfirmationModal from "../../components/ConfirmationModal";
import SortableTableHeader from "../../components/SortableTableHeader";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";

const APPCreditWallets = () => {
  const token = useSelector((state) => state.auth.access_token);
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    pageSize: 15,
    EndDate: "",
    order: "",
    username: "",
  });
  const [tempFilters, setTempFilters] = useState({
    page: 1,
    pageSize: 15,
    EndDate: "",
    order: "",
    username: "",
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalRows: 0,
  });
  const [sort, setSort] = useState({ key: "", direction: "asc" });
  const [sortedWallets, setSortedWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const { can } = usePermissions();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [showBalanceModal, setShowBalanceModal] = useState(false);
  const [showAddConfirmModal, setShowAddConfirmModal] = useState(false);
  const [showDeductConfirmModal, setShowDeductConfirmModal] = useState(false);
  const [amount, setAmount] = useState("");
  // Admins only: the user's statement line is written by the server.
  const [note, setNote] = useState("");
  const [balance, setBalance] = useState(null);
  const [loadingAction, setLoadingAction] = useState(false);

  const fetchWallets = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.EndDate) params.EndDate = filters.EndDate;
      if (filters.order) params.order = filters.order;
      if (filters.username) params.username = filters.username;
      const res = await axiosInstance.get("/api/admin/wallets/APP_CREDIT", {
        params,
        headers: { token },
      });
      const newData = res.data.data.data;
      setWallets(newData);
      setSortedWallets(newData);
      setPagination({
        currentPage: Number(filters.page) || 1,
        totalPages: res.data.data.totalPages,
        totalRows: res.data.data.totalRows,
      });
    } catch (err) {
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (!wallets.length) return;

    const sorted = [...wallets].sort((a, b) => {
      if (!sort.key) return 0;

      let aValue = a[sort.key];
      let bValue = b[sort.key];

      if (aValue === null || aValue === undefined) aValue = "";
      if (bValue === null || bValue === undefined) bValue = "";

      if (sort.key === "amount") {
        aValue = parseFloat(aValue) || 0;
        bValue = parseFloat(bValue) || 0;
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      if (sort.key === "createdAt" || sort.key === "updatedAt") {
        aValue = new Date(aValue).getTime();
        bValue = new Date(bValue).getTime();
        return sort.direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    });

    setSortedWallets(sorted);
  }, [wallets, sort]);

  const handleTempFilterChange = (e) => {
    const { name, value } = e.target;
    setTempFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    setFilters(tempFilters);
  };

  const clearFilters = () => {
    const defaultFilters = {
      page: 1,
      pageSize: 15,
      EndDate: "",
      order: "",
      username: "",
    };
    setFilters(defaultFilters);
    setTempFilters(defaultFilters);
  };

  const handleSort = (key) => {
    setSort((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleAddBalance = async (e) => {
    e.preventDefault();
    if (!amount || !selectedWallet) return;
    setShowAddConfirmModal(true);
  };

  const confirmAddBalance = async () => {
    setLoadingAction(true);
    try {
      const res = await axiosInstance.post(
        "/api/admin/wallets/APP_CREDIT/add-balance",
        { username: selectedWallet.username, amount, note: note.trim() },
        { headers: { token } }
      );
      if (res.data.success) {
        toast.success(`Added ${amount} APP Credit to ${selectedWallet.username}`);
        setShowAddModal(false);
        setShowAddConfirmModal(false);
        setAmount("");
        setNote("");
        fetchWallets();
      } else {
        toast.error(res.data.message || "Failed to add balance");
      }
    } catch (err) {
      toast.error("Error adding balance");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleDeductBalance = async (e) => {
    e.preventDefault();
    if (!amount || !selectedWallet) return;
    setShowDeductConfirmModal(true);
  };

  const confirmDeductBalance = async () => {
    setLoadingAction(true);
    try {
      const res = await axiosInstance.post(
        "/api/admin/wallets/APP_CREDIT/deduct-balance",
        { username: selectedWallet.username, amount, note: note.trim() },
        { headers: { token } }
      );
      if (res.data.success) {
        toast.success(`Deducted ${amount} APP Credit from ${selectedWallet.username}`);
        setShowDeductModal(false);
        setShowDeductConfirmModal(false);
        setAmount("");
        setNote("");
        fetchWallets();
      } else {
        toast.error(res.data.message || "Failed to deduct balance");
      }
    } catch (err) {
      toast.error("Error deducting balance");
    } finally {
      setLoadingAction(false);
    }
  };

  const handleCheckBalance = async () => {
    if (!selectedWallet) return;
    setLoadingAction(true);
    try {
      const res = await axiosInstance.get(
        `/api/admin/wallets/APP_CREDIT/available-balance?username=${selectedWallet.username}`,
        { headers: { token } }
      );
      if (res.data.success) {
        setBalance(res.data.data.balance);
        setShowBalanceModal(true);
      } else {
        toast.error(res.data.message || "Failed to fetch balance");
      }
    } catch (err) {
      toast.error("Error fetching balance");
    } finally {
      setLoadingAction(false);
    }
  };

  const ActionDropdown = ({ wallet }) => (
    <div className="inline-block">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
            aria-label="Wallet actions"
            type="button"
          >
            <MoreHorizontal className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-48">
          {can("POST:/api/admin/wallets/:wallet_type/add-balance") && (
            <DropdownMenuItem
              onSelect={() => {
                setSelectedWallet(wallet);
                setShowAddModal(true);
              }}
            >
              Add Balance
            </DropdownMenuItem>
          )}
          {can("POST:/api/admin/wallets/:wallet_type/deduct-balance") && (
            <DropdownMenuItem
              onSelect={() => {
                setSelectedWallet(wallet);
                setShowDeductModal(true);
              }}
            >
              Deduct Balance
            </DropdownMenuItem>
          )}
          {can("GET:/api/admin/wallets/:wallet_type/available-balance") && (
            <DropdownMenuItem
              onSelect={() => {
                setSelectedWallet(wallet);
                handleCheckBalance();
              }}
            >
              Check Balance
            </DropdownMenuItem>
          )}
          {can("GET:/api/admin/reports/statement/:wallet_type") && (
            <DropdownMenuItem
              onSelect={() =>
                navigate(
                  `/reports/wallet-statement?username=${wallet.username}&walletType=APP_CREDIT`
                )
              }
            >
              Check Statement
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
        <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
        Finance
      </div>
      <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">
        Credit <span className="text-gradient-ginox">Wallets</span>
      </h1>

      {/* Filters */}
      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              End Date
            </label>
            <input
              type="date"
              name="EndDate"
              value={tempFilters.EndDate}
              onChange={handleTempFilterChange}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Username / WalletAddress
            </label>
            <input
              type="text"
              name="username"
              value={tempFilters.username}
              onChange={handleTempFilterChange}
              placeholder="Search…"
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            />
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Order
            </label>
            <select
              name="order"
              value={tempFilters.order}
              onChange={handleTempFilterChange}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value="">Order</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page Size
            </label>
            <select
              name="pageSize"
              value={tempFilters.pageSize}
              onChange={handleTempFilterChange}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              <option value={15}>15</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
              Page
            </label>
            <select
              name="page"
              value={tempFilters.page}
              onChange={handleTempFilterChange}
              className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
            >
              {Array.from({ length: pagination.totalPages }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {i + 1}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={applyFilters}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
          >
            Apply Filters
          </button>
          <button
            onClick={clearFilters}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader
                sortKey="sno"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                S.No
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="username"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Username
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="amount"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Amount
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="type"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Type
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="createdAt"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Created At
              </SortableTableHeader>
              <SortableTableHeader
                sortKey="updatedAt"
                currentSort={sort}
                onSort={handleSort}
                className="text-left"
              >
                Updated At
              </SortableTableHeader>
              <th className="border-b border-border/70 px-5 py-3 text-[10px] font-medium uppercase tracking-[1.5px] text-[#9aa1ac] text-left">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">
                  Loading...
                </td>
              </tr>
            ) : (
              sortedWallets.map((wallet, index) => (
                <tr className="group hover:bg-white/[0.02] transition-colors" key={wallet._id}>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {(filters.page - 1) * filters.pageSize + index + 1}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {wallet.username}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {wallet.amount}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    {wallet.type}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(wallet.createdAt).toLocaleString()}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground font-mono">
                    {new Date(wallet.updatedAt).toLocaleString()}
                  </td>
                  <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">
                    <ActionDropdown wallet={wallet} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        totalRows={pagination.totalRows}
        pageSize={filters.pageSize}
        onPageChange={(newPage) =>
          setFilters((prev) => ({ ...prev, page: newPage }))
        }
      />

      {/* Add Balance Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border/70 bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif italic text-lg text-foreground">Add Balance</h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleAddBalance}>
              <div className="mb-4">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Username
                </label>
                <p className="text-sm text-foreground font-mono mb-4">{selectedWallet?.username}</p>
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                  required
                />
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5 mt-4">
                  Note <span className="normal-case tracking-normal text-muted-foreground/60">(optional, admins only)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why is this balance being changed?"
                  rows={3}
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
              </div>
              <button
                type="submit"
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors"
                disabled={loadingAction}
              >
                {loadingAction ? "Adding..." : "Add Balance"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Deduct Balance Modal */}
      {showDeductModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border/70 bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif italic text-lg text-foreground">Deduct Balance</h2>
              <button
                onClick={() => setShowDeductModal(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={handleDeductBalance}>
              <div className="mb-4">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Username
                </label>
                <p className="text-sm text-foreground font-mono mb-4">{selectedWallet?.username}</p>
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Amount
                </label>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                  required
                />
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5 mt-4">
                  Note <span className="normal-case tracking-normal text-muted-foreground/60">(optional, admins only)</span>
                </label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why is this balance being changed?"
                  rows={3}
                  className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                />
              </div>
              <button
                type="submit"
                className="rounded-md border border-red-500/60 bg-red-500/10 px-4 py-2 text-xs font-medium text-red-400 hover:bg-red-500/20 transition-colors"
                disabled={loadingAction}
              >
                {loadingAction ? "Deducting..." : "Deduct Balance"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Check Balance Modal */}
      {showBalanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl border border-border/70 bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-serif italic text-lg text-foreground">Available Credits</h2>
              <button
                onClick={() => setShowBalanceModal(false)}
                className="flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-white/[0.05] hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="mb-5">
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1">Username</p>
              <p className="text-sm text-foreground font-mono mb-4">{selectedWallet?.username}</p>
              <p className="text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1">Balance</p>
              <p className="text-2xl font-mono text-foreground">{balance} <span className="text-sm text-muted-foreground">Credits</span></p>
            </div>
            <button
              className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
              onClick={() => setShowBalanceModal(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showAddConfirmModal}
        onClose={() => setShowAddConfirmModal(false)}
        onConfirm={confirmAddBalance}
        title="Add Balance"
        message={`Are you sure you want to add ${amount} APP Credit to ${selectedWallet?.username}?`}
        confirmText="Add Balance"
        loading={loadingAction}
        loadingText="Adding..."
      />

      <ConfirmationModal
        isOpen={showDeductConfirmModal}
        onClose={() => setShowDeductConfirmModal(false)}
        onConfirm={confirmDeductBalance}
        title="Deduct Balance"
        message={`Are you sure you want to deduct ${amount} APP Credit from ${selectedWallet?.username}?`}
        confirmText="Deduct Balance"
        loading={loadingAction}
        loadingText="Deducting..."
        danger={true}
      />
    </div>
  );
};

export default APPCreditWallets;
